import is from '@sindresorhus/is';
import type { FunctionComponent, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconCheckmark1Small, IconCrossSmall } from '~/ui/core/icons';
import { Textarea } from '~/ui/core/textarea/Textarea';

import { useDismissOnOutsideMousedown } from '../../chat/hooks/use-dismiss-on-outside-mousedown';
import { isVersionNoteLockedError, useEditVersionNote } from '../api/use-edit-version-note';
import { VersionNoteLine } from './VersionNoteLine';

type EditableVersionNoteProps = {
    readonly actionsVariant?: 'icon' | 'text';
    readonly architectureId: string;
    readonly isEditing: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly layout?: 'default' | 'expanded-action-bar';
    readonly note: string | undefined;
    readonly onDisplayExpandedChange?: (expanded: boolean) => void;
    readonly onEnterEditMode: () => void;
    readonly onExitEditMode: () => void;
    readonly version: number;
};

type EditableVersionNoteLayout = NonNullable<EditableVersionNoteProps['layout']>;
type EditableVersionNoteActionsVariant = NonNullable<EditableVersionNoteProps['actionsVariant']>;

const editorGapClass = (
    actionsVariant: EditableVersionNoteActionsVariant,
    layout: EditableVersionNoteLayout,
): string => {
    if (actionsVariant !== 'text') {
        return 'gap-xxs';
    }

    return layout === 'expanded-action-bar' ? 'gap-xs' : 'gap-s';
};

const textareaClassName = (
    actionsVariant: EditableVersionNoteActionsVariant,
    layout: EditableVersionNoteLayout,
): string => {
    if (actionsVariant !== 'text') {
        return '!px-s !py-xs';
    }

    return layout === 'expanded-action-bar' ? 'block min-h-[96px]' : 'block';
};

const textareaRows = (layout: EditableVersionNoteLayout): number => (layout === 'expanded-action-bar' ? 3 : 2);

const textActionClassName = (layout: EditableVersionNoteLayout): string =>
    `flex items-center justify-end gap-xs ${
        layout === 'expanded-action-bar' ? 'pb-[calc(var(--spacing-s)+var(--spacing-m)-var(--spacing-xxs))] pt-xs' : ''
    }`;

type EditorActionsProps = {
    readonly actionsVariant: EditableVersionNoteActionsVariant;
    readonly isSaving: boolean;
    readonly layout: EditableVersionNoteLayout;
    readonly onCancel: () => void;
    readonly onSave: () => void;
};

const EditorActions: FunctionComponent<EditorActionsProps> = ({
    actionsVariant,
    isSaving,
    layout,
    onCancel,
    onSave,
}) => {
    const translate = useTranslate();

    if (actionsVariant === 'text') {
        return (
            <div className={textActionClassName(layout)}>
                <Button
                    aria-label={translate('pages.job-architecture-v2.version-note.cancel')}
                    disabled={isSaving}
                    size="sm"
                    type="button"
                    variant="tertiary"
                    onClick={onCancel}
                >
                    {translate('pages.job-architecture-v2.version-note.cancel')}
                </Button>
                <Button
                    aria-label={translate('pages.job-architecture-v2.version-note.save')}
                    disabled={isSaving}
                    size="sm"
                    type="button"
                    variant="solid"
                    onClick={onSave}
                >
                    {translate('pages.job-architecture-v2.version-note.save')}
                </Button>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-end gap-[2px]">
            <Button
                aria-label={translate('pages.job-architecture-v2.version-note.cancel')}
                disabled={isSaving}
                iconOnly={true}
                size="sm"
                type="button"
                variant="tertiary"
                onClick={onCancel}
            >
                <IconCrossSmall height={14} width={14} />
            </Button>
            <Button
                aria-label={translate('pages.job-architecture-v2.version-note.save')}
                disabled={isSaving}
                iconOnly={true}
                size="sm"
                type="button"
                variant="tertiary"
                onClick={onSave}
            >
                <IconCheckmark1Small height={14} width={14} />
            </Button>
        </div>
    );
};

export const EditableVersionNote: FunctionComponent<EditableVersionNoteProps> = ({
    actionsVariant = 'icon',
    architectureId,
    isEditing,
    itemId,
    itemType,
    layout = 'default',
    note,
    onDisplayExpandedChange,
    onEnterEditMode,
    onExitEditMode,
    version,
}) => {
    const translate = useTranslate();
    const editor = useRef<HTMLDivElement | null>(null);
    const textarea = useRef<HTMLTextAreaElement | null>(null);
    const wasEditingRef = useRef(isEditing);

    const [draft, setDraft] = useState(note ?? '');

    const mutation = useEditVersionNote();
    const isSaving = mutation.isPending;

    useEffect(() => {
        if (isEditing && !wasEditingRef.current) {
            setDraft(note ?? '');
        }
        wasEditingRef.current = isEditing;
    }, [isEditing, note]);

    useEffect(() => {
        if (isEditing && textarea.current !== null) {
            textarea.current.focus();
            textarea.current.setSelectionRange(textarea.current.value.length, textarea.current.value.length);
        }
    }, [isEditing]);

    useDismissOnOutsideMousedown({
        active: isEditing,
        containerRef: editor,
        onDismiss: onExitEditMode,
    });

    const handleSave = (): void => {
        const trimmed = draft.trim();
        const original = note ?? '';

        if (trimmed === original) {
            onExitEditMode();
            return;
        }

        mutation.mutate(
            {
                architectureId,
                comment: trimmed.length === 0 ? null : trimmed,
                itemId,
                itemType,
                version: String(version),
            },
            {
                onSuccess: () => {
                    onExitEditMode();
                },
                onError: (error) => {
                    if (isVersionNoteLockedError(error)) {
                        onExitEditMode();
                    }
                },
            },
        );
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
        if (event.key === 'Escape') {
            event.preventDefault();
            onExitEditMode();
            return;
        }

        if (event.key === 'Enter' && !event.shiftKey) {
            if (event.nativeEvent.isComposing) {
                return;
            }
            event.preventDefault();
            handleSave();
        }
    };

    if (isEditing) {
        return (
            <div
                ref={editor}
                className={`flex w-full min-w-0 flex-col items-stretch ${editorGapClass(actionsVariant, layout)}`}
            >
                <Textarea
                    ref={textarea}
                    autoResize={true}
                    aria-label={translate('pages.job-architecture-v2.version-note.aria-label-input')}
                    className={textareaClassName(actionsVariant, layout)}
                    disabled={isSaving}
                    maxRows={8}
                    placeholder={translate('pages.job-architecture-v2.version-note.placeholder')}
                    rows={textareaRows(layout)}
                    value={draft}
                    variant="default"
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <EditorActions
                    actionsVariant={actionsVariant}
                    isSaving={isSaving}
                    layout={layout}
                    onCancel={onExitEditMode}
                    onSave={handleSave}
                />
            </div>
        );
    }

    if (is.nonEmptyString(note)) {
        return <VersionNoteLine note={note} onExpandedChange={onDisplayExpandedChange} />;
    }

    return (
        <Button className="!px-0 !py-0" size="sm" type="button" variant="link" onClick={onEnterEditMode}>
            {translate('pages.job-architecture-v2.version-note.add')}
        </Button>
    );
};
